// The web kit's gallery (dev builds): every component of components/ui in each kind, size and state, with the theme
// and font-scale switches of the Compose GalleryScreen. The samples' words are verbatim (a developer screen) and their
// ids literal `gallery_…`: the catalogue has no kit stems.
import { ids } from '@orca/api-client/testIds'
import { createFileRoute } from '@tanstack/react-router'
import { type ComponentProps, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Alert } from '../../components/ui/alert'
import { AuditRow } from '../../components/ui/audit-row'
import { Banner } from '../../components/ui/banner'
import { BulkBar } from '../../components/ui/bulk-bar'
import { Button, type ButtonVariant, type ControlSize } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Check } from '../../components/ui/check'
import { DOTS } from '../../components/ui/chip'
import { cn } from '../../components/ui/cn'
import { ContextMenu } from '../../components/ui/context-menu'
import {
  type Column,
  DataTable,
  pageRows,
  type SortState,
  sortRows,
  type TableRow,
} from '../../components/ui/data-table'
import { DescriptionList } from '../../components/ui/description-list'
import { DetailPane, DetailSplit } from '../../components/ui/detail-pane'
import { DeviceRow } from '../../components/ui/device-row'
import { IconButton } from '../../components/ui/icon-button'
import { Icon } from '../../components/ui/icons'
import { InboxRow } from '../../components/ui/inbox-row'
import { InvitationCard } from '../../components/ui/invitation-card'
import { ListEnd } from '../../components/ui/list-end'
import { ListRow } from '../../components/ui/list-row'
import { OrgCard } from '../../components/ui/org-card'
import { Presence, PresenceStack } from '../../components/ui/presence'
import { Progress } from '../../components/ui/progress'
import { ProgressRing } from '../../components/ui/progress-ring'
import { SectionHeader } from '../../components/ui/section-header'
import { Select } from '../../components/ui/select'
import { Sheet } from '../../components/ui/sheet'
import { SidePanel } from '../../components/ui/side-panel'
import { Skeleton } from '../../components/ui/skeleton'
import { Snackbar, showSnackbar } from '../../components/ui/snackbar'
import { TabPanel, Tabs } from '../../components/ui/tabs'
import { TextField } from '../../components/ui/text-field'
import { UnsentRow } from '../../components/ui/unsent-row'
import { useTheme } from '../../shared/theme/ThemeProvider'
import { nextTheme } from '../../shared/theme/theme'
import { AccessParts } from './-access-parts'
import { FormParts } from './-form-parts'
import { InputParts } from './-input-parts'
import { NumberParts } from './-number-parts'
import { OverlayParts } from './-overlay-parts'
import { StateParts } from './-state-parts'
import { StatusParts } from './-status-parts'

export const Route = createFileRoute('/gallery/')({
  staticData: { screenId: 'GalleryScreen', access: 'dev builds' },
  component: GalleryScreen,
})

const variants: ButtonVariant[] = ['primary', 'secondary', 'tonal', 'ghost', 'danger', 'glass', 'white']
const sizes: ControlSize[] = ['sm', 'md', 'lg']
const teams = ['Kitchen', 'Floor', 'Bar']
type GalleryMember = TableRow & { name: string; role: string; since: string }
const galleryMembers: GalleryMember[] = [
  { id: 'dana', name: 'Dana Whitlock', role: 'Member', since: '2026-03-02' },
  { id: 'alex', name: 'Alex Rivera', role: 'Owner', since: '2025-11-18' },
  { id: 'casey', name: 'Casey Moreno', role: 'Member', since: '2026-05-09' },
  { id: 'blair', name: 'Blair Hu', role: 'Manager', since: '2026-01-27' },
  { id: 'eli', name: 'Eli Novak', role: 'Member', since: '2026-08-14' },
]
const memberColumns: Column<GalleryMember>[] = [
  { key: 'name', label: 'Name', type: 'person', sortable: true },
  { key: 'role', label: 'Role', type: 'role' },
  { key: 'since', label: 'Joined', type: 'meta', sortable: true },
]
type GalleryEntry = TableRow & { actor: string; action: string; when: string }
const galleryEntries: GalleryEntry[] = [
  { id: 'e1', actor: 'Dana Whitlock', action: 'Renamed the team Kitchen', when: '09:12' },
  { id: 'e2', actor: 'Sam Okafor', action: 'Changed Eli Novak to Manager', when: '08:40', support: true },
  { id: 'e3', actor: 'Alex Rivera', action: 'Invited casey@northwind.example', when: 'Yesterday' },
]
const entryColumns: Column<GalleryEntry>[] = [
  { key: 'actor', label: 'Who', type: 'person' },
  { key: 'action', label: 'What' },
  { key: 'when', label: 'When', type: 'meta' },
]
const PAGE_SIZE = 2
const tabs = [
  { value: 'comments', label: 'Comments', testId: 'gallery_commentsTab' },
  { value: 'history', label: 'History', testId: 'gallery_historyTab' },
  { value: 'files', label: 'Files', testId: 'gallery_filesTab' },
]

function GalleryScreen() {
  const { theme, setTheme } = useTheme()
  const [largeText, setLargeText] = useState(false)
  const [sideBySide, setSideBySide] = useState(false)

  useEffect(() => {
    // The browser's own text scale is what a person changes; this stands in for it (200 % is WCAG's bar).
    document.documentElement.style.fontSize = largeText ? '200%' : ''
    return () => {
      document.documentElement.style.fontSize = ''
    }
  }, [largeText])

  return (
    <main
      data-testid={ids.gallery.root}
      className={cn(
        'mx-auto flex flex-col gap-(--stack-lg) p-(--inset-lg)',
        sideBySide ? 'w-full' : 'max-w-(--reading-width-expanded)',
      )}
    >
      <header className="flex flex-wrap items-center gap-(--inline-md)">
        <h1 className="flex-1 font-bold text-title-lg">Gallery</h1>
        <Button
          variant="secondary"
          size="sm"
          data-testid={ids.gallery.themeSwitch}
          onClick={() => setTheme(nextTheme(theme))}
        >
          Theme: {theme}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={largeText}
          data-testid={ids.gallery.fontScaleSwitch}
          onClick={() => setLargeText(!largeText)}
        >
          Text 200 %
        </Button>
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={sideBySide}
          data-testid="gallery_sideBySideButton"
          onClick={() => setSideBySide(!sideBySide)}
        >
          Light and dark
        </Button>
      </header>

      {sideBySide ? (
        // Each pane its own list, so a dialog opens in the pane it was asked in; the dark pane's test ids end in `_dark`,
        // so the page repeats none.
        <div className="grid gap-(--stack-lg) expanded:grid-cols-2">
          {(['light', 'dark'] as const).map((it) => (
            <Pane
              key={it}
              suffix={it === 'dark' ? DARK_SUFFIX : ''}
              aria-label={it === 'light' ? 'Light' : 'Dark'}
              data-theme={it}
              data-testid={`gallery_${it}Pane`}
              className="min-w-0 rounded-(--radius-dialog) border border-line bg-canvas p-(--inset-lg) text-fg"
            >
              <GalleryList />
            </Pane>
          ))}
        </div>
      ) : (
        <GalleryList />
      )}
    </main>
  )
}

const DARK_SUFFIX = '_dark'

/** A pane whose test ids (also those of nodes added later) end in `suffix`, so two panes of one list repeat none. */
function Pane({ suffix, children, ...rest }: { suffix: string } & ComponentProps<'section'>) {
  const ref = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const root = ref.current
    if (!root || !suffix) return
    const mark = (node: Element) => {
      for (const it of [node, ...node.querySelectorAll('[data-testid]')]) {
        if (it === root) continue
        const id = it.getAttribute('data-testid')
        if (id && !id.endsWith(suffix)) it.setAttribute('data-testid', id + suffix)
      }
    }
    mark(root)
    const observer = new MutationObserver((records) => {
      for (const r of records) {
        if (r.type === 'attributes' && r.target instanceof Element) mark(r.target)
        for (const n of r.addedNodes) if (n instanceof Element) mark(n)
      }
    })
    observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-testid'] })
    return () => observer.disconnect()
  }, [suffix])
  return (
    <section ref={ref} {...rest}>
      {children}
    </section>
  )
}

/** Every sample, with the state its controls change; the side-by-side view draws one per theme. */
function GalleryList() {
  const [dialog, setDialog] = useState<'plain' | 'destructive' | null>(null)
  const [email, setEmail] = useState('ada@')
  const [selected, setSelected] = useState('tasks')
  const [tab, setTab] = useState('comments')
  const [team, setTeam] = useState<string | null>(null)
  const [chosenTeam, setChosenTeam] = useState<string | null>('Floor')
  const [assignee, setAssignee] = useState<string | null>(null)
  const [dotTeam, setDotTeam] = useState<string | null>('Bar')
  const [sheet, setSheet] = useState<'plain' | 'reauth' | null>(null)
  const [banners, setBanners] = useState(true)
  const [uploaded, setUploaded] = useState(5)
  const [checks, setChecks] = useState(0)
  const [sort, setSort] = useState<SortState | null>(null)
  const [page, setPage] = useState(0)
  const [openMember, setOpenMember] = useState<GalleryMember | null>(null)
  const [status, setStatus] = useState('todo')
  const sortedMembers = sortRows(galleryMembers, sort)
  const [actor, setActor] = useState<string | null>(null)
  const shownEntries = actor ? galleryEntries.filter((it) => it.id === actor) : galleryEntries

  return (
    <div data-testid={ids.gallery.list} className="flex flex-col gap-(--stack-xl)">
      <Section title="Button">
        <div className="flex flex-wrap items-center gap-(--inline-md) pb-(--press-travel)">
          {variants.map((variant) => (
            <HeaderGround key={variant} on={variant === 'glass'}>
              <Button variant={variant} data-testid={`gallery_${variant}Button`}>
                {variant[0]?.toUpperCase() + variant.slice(1)}
              </Button>
            </HeaderGround>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-(--inline-md) pb-(--press-travel)">
          {sizes.map((size) => (
            <Button key={size} size={size}>
              Size {size}
            </Button>
          ))}
          <Button loading>Saving</Button>
          <Button disabled>Not now</Button>
        </div>
      </Section>

      <Section title="Icon button">
        <div className="flex flex-wrap items-center gap-(--inline-md)">
          <IconButton aria-label="Open menu" data-testid="gallery_menuButton">
            <Icon name="menu" aria-hidden />
          </IconButton>
          <HeaderGround>
            <IconButton aria-label="Close" variant="glass" data-testid="gallery_glassIconButton">
              <Icon name="close" aria-hidden />
            </IconButton>
          </HeaderGround>
          <IconButton aria-label="Add" variant="filled">
            <Icon name="plus" aria-hidden />
          </IconButton>
          <IconButton aria-label="Closed" disabled>
            <Icon name="close" aria-hidden />
          </IconButton>
        </div>
      </Section>

      <Section title="Text field">
        <TextField label="Name" helperText="As your team will see it" placeholder="Ada Lovelace" />
        <TextField
          label="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          errorText={email.includes('.') ? undefined : 'Enter a valid email address'}
        />
        <TextField label="Password" type="password" helperText="At least 12 characters" />
        <TextField label="Seats" inputMode="numeric" defaultValue="12" suffix="seats" />
        <TextField label="Organization" defaultValue="Northwind" disabled />
      </Section>

      <FormParts Section={Section} />
      <InputParts Section={Section} />
      <OverlayParts Section={Section} />

      <Section title="Card">
        <div className="grid gap-(--stack-md) medium:grid-cols-3">
          <Card>
            <strong>Raised</strong>
            <span className="text-fg-muted">One subject on its own panel.</span>
          </Card>
          <Card level="sunken">
            <strong>Sunken</strong>
            <span className="text-fg-muted">A read-only well.</span>
          </Card>
          <Card level="base">
            <strong>Base</strong>
            <span className="text-fg-muted">Groups only.</span>
          </Card>
        </div>
      </Section>

      <Section title="List row">
        <Card level="raised" className="p-0">
          <ul className="divide-y divide-line">
            <ListRow
              headline="Profile"
              supporting="Name, photo and language"
              leading={<Icon name="user" />}
              onClick={() => {}}
            />
            <ListRow headline="Plan" supporting="Renews 1 Nov" trailing="Team, 12 seats" />
            {['tasks', 'members'].map((key) => (
              <ListRow
                key={key}
                headline={key === 'tasks' ? 'Tasks' : 'Members'}
                leading={<Icon name="bell" />}
                selected={selected === key}
                onClick={() => setSelected(key)}
              />
            ))}
            <ListRow headline="Leave organization" destructive onClick={() => setDialog('destructive')} />
          </ul>
        </Card>
      </Section>

      <Section title="Lists">
        <SectionHeader title="This week" count={3} action={{ label: 'See all', href: '#' }} />
        <Card level="raised" className="p-0">
          <ul className="divide-y divide-line">
            <ListRow
              headline="Order seals"
              supporting="Kitchen · due Friday"
              onClick={() => {}}
              showActions
              actions={[
                { icon: 'edit', label: 'Edit Order seals', onClick: () => {} },
                { icon: 'trash', label: 'Delete Order seals', onClick: () => {} },
              ]}
            />
            <InboxRow
              title={
                <>
                  <b>Sam Okafor</b> assigned you <b>Order seals</b>
                </>
              }
              subtitle="Kitchen"
              time="10:42"
              unread
              avatar="Sam Okafor"
              testId="gallery_unreadItem"
            />
            <InboxRow
              title={
                <>
                  <b>Alex Rivera</b> invited you to <b>Northwind</b>
                </>
              }
              time="Yesterday"
              org="Northwind"
              actions={
                <>
                  <Button size="sm">Accept</Button>
                  <Button size="sm" variant="secondary">
                    Decline
                  </Button>
                </>
              }
            />
            <AuditRow actor="Rin Aoki" time="09:10" support meta="Orca staff · “sign-in help”" onOpen={() => {}}>
              signed in as Alex Rivera
            </AuditRow>
            <AuditRow actor="Alex Rivera" time="08:02" meta="Members" href="#">
              changed Sam Okafor’s role to Manager
            </AuditRow>
            <DeviceRow name="MacBook Air" detail="Safari · Prague · 4 h ago" icon="laptop" onSignOut={() => {}} />
            <DeviceRow name="Pixel 9" detail="Orca for Android · Brno · now" current />
            <li>
              <ListEnd state="end" count={7} noun="entries" />
            </li>
          </ul>
        </Card>
        <div className="flex flex-col gap-(--stack-sm)">
          <ListEnd state="loading" />
          <ListEnd state="more" count={20} onMore={() => {}} />
          <ListEnd state="failed" onRetry={() => {}} />
        </div>
        <UnsentRow
          title="Status → Done"
          where="Order seals"
          reason="Someone else changed it"
          reasonIcon="alert"
          reasonTone="warn"
          actions={[
            { label: 'Keep mine', variant: 'primary', onClick: () => {} },
            { label: 'Discard', onClick: () => {} },
          ]}
          testId="gallery_unsentCard"
        />
        <OrgCard name="Northwind" subtitle="Manager · 6 people" href="#" onMore={() => {}} />
        <OrgCard name="Kitchen Crew" subtitle="Invited by Alex" invitation />
        <OrgCard name="Old Org" subtitle="Deleting" disabled />
        <InvitationCard
          org="Northwind"
          inviter="Alex Rivera"
          roleName="Manager"
          team={{ name: 'Kitchen', color: 3 }}
          email="sam@northwind.test"
          labels={{ invitedBy: 'Invited by', role: 'Role', team: 'Team', email: 'Email' }}
        >
          <Button>Join Northwind</Button>
        </InvitationCard>
        <DescriptionList
          items={[
            { term: 'Status', value: 'Doing' },
            { term: 'Assignee', value: 'Sam Okafor' },
            { term: 'Notes', value: 'Ordered from the usual supplier; check the invoice.', multiline: true },
          ]}
        />
        <div className="flex flex-wrap items-center gap-(--inline-md)">
          <Presence status="here" name="Sam Okafor" caption testId="gallery_hereValue" />
          <Presence status="away" name="Jordan Lee" caption />
          <Presence status="offline" name="Riley Park" caption />
          <PresenceStack people={['Sam', 'Jordan', 'Riley']} testId="gallery_stackValue" />
          <PresenceStack people={['Sam', 'Jordan', 'Riley', 'Kim', 'Lee', 'Max', 'Ola']} testId="gallery_crowdValue" />
        </div>
      </Section>

      <Section title="Data table">
        <div className="flex flex-wrap items-start gap-(--stack-md)">
          <div className="flex min-w-0 flex-1 flex-col gap-(--stack-md)">
            <DataTable
              label="Members"
              testId="gallery_membersList"
              columns={memberColumns}
              rows={pageRows(sortedMembers, page, PAGE_SIZE)}
              sort={sort}
              onSort={setSort}
              selectedId={openMember?.id}
              onOpen={setOpenMember}
              search={false}
              filters={{ labels: ['All', 'Owners'], value: 'All', onChange: () => {} }}
              summary={`${page * PAGE_SIZE + 1}–${Math.min(sortedMembers.length, (page + 1) * PAGE_SIZE)} of ${sortedMembers.length}`}
              hasPrev={page > 0}
              hasNext={(page + 1) * PAGE_SIZE < sortedMembers.length}
              onPage={(direction) => setPage(page + (direction === 'next' ? 1 : -1))}
            />
            <DataTable
              label="Audit log"
              testId="gallery_auditList"
              columns={entryColumns}
              rows={shownEntries}
              search={false}
              person={{
                label: 'Person',
                people: galleryEntries.map((it) => ({ id: it.id, name: it.actor })),
                value: actor,
                onChange: setActor,
              }}
              dense
              pager={false}
            />
          </div>
          {openMember && (
            <SidePanel
              title={openMember.name}
              testId="gallery_memberSheet"
              avatar={openMember.name}
              lines={[`${openMember.role} · since ${openMember.since}`]}
              selects={[{ label: 'Role', value: openMember.role }]}
              checksTitle="What they can do"
              checks={[
                { label: 'Create tasks', allowed: true },
                { label: 'Remove members', allowed: openMember.role !== 'Member' },
              ]}
              actions={[{ label: 'Remove from Northwind', variant: 'danger', onClick: () => setOpenMember(null) }]}
              onClose={() => setOpenMember(null)}
            />
          )}
        </div>
      </Section>

      <Section title="Detail pane">
        {/* DetailSplit (E22U8): the list and the pane scroll on their own; the separator drags or takes the arrows. */}
        <div className="flex h-[32rem]">
          <DetailSplit
            label="Resize the detail"
            storageKey="orca.gallery.detailWidth"
            list={
              <div className="flex flex-col gap-2">
                {['Order seals', 'Check the cold room', 'Wipe the counters', 'Restock the bar'].map((title) => (
                  <div key={title} className="rounded-(--radius-card) bg-surface p-(--inset-md) text-fg text-label">
                    {title}
                  </div>
                ))}
              </div>
            }
            detail={
              <DetailPane
                title="Order seals"
                testId="gallery_taskCard"
                statuses={[
                  { value: 'todo', label: 'To do' },
                  { value: 'doing', label: 'Doing' },
                  { value: 'done', label: 'Done' },
                ]}
                status={status}
                onStatus={setStatus}
                items={[
                  { term: 'Assignee', value: 'Sam Okafor' },
                  { term: 'Due', value: 'Friday' },
                ]}
                description="Ordered from the usual supplier."
                composer={{ label: 'Write a comment', sendLabel: 'Send', onSend: () => {} }}
              />
            }
          />
        </div>
      </Section>

      <Section title="Bulk bar">
        <div>
          <BulkBar
            floating={false}
            count={2}
            actions={[
              { icon: 'check', label: 'Done', onClick: () => {} },
              { icon: 'trash', label: 'Delete', danger: true, onClick: () => {} },
            ]}
            onClear={() => {}}
          />
        </div>
      </Section>

      <Section title="Dialog">
        <div className="flex flex-wrap gap-(--inline-md) pb-(--press-travel)">
          <Button variant="secondary" onClick={() => setDialog('plain')}>
            Open dialog
          </Button>
          <Button variant="danger" onClick={() => setDialog('destructive')}>
            Open destructive dialog
          </Button>
        </div>
        <Alert
          open={dialog === 'plain'}
          title="Publish the plan?"
          message="Everyone in Northwind sees it from tomorrow."
          confirmLabel="Publish plan"
          declineLabel="Not yet"
          onConfirm={() => setDialog(null)}
          onDismiss={() => setDialog(null)}
          confirmTestId="gallery_publishButton"
          declineTestId="gallery_notYetButton"
        />
        <Alert
          open={dialog === 'destructive'}
          title="Leave Northwind?"
          message="You lose access to its tasks until someone invites you again."
          confirmLabel="Leave organization"
          declineLabel="Stay"
          destructive
          onConfirm={() => setDialog(null)}
          onDismiss={() => setDialog(null)}
        />
      </Section>

      <Section title="Tabs">
        <Tabs label="Task detail" tabs={tabs} value={tab} onValueChange={setTab}>
          <TabPanel value="comments">No comments yet.</TabPanel>
          <TabPanel value="history">Created today by Ada.</TabPanel>
          <TabPanel value="files">No files attached.</TabPanel>
        </Tabs>
      </Section>

      <Section title="Menu">
        <div className="flex">
          <ContextMenu
            trigger={
              <Button variant="secondary" data-testid="gallery_moreButton">
                More
              </Button>
            }
            items={[
              { label: 'Edit', icon: <Icon name="edit" />, onSelect: () => {}, testId: 'gallery_editItem' },
              { label: 'Copy link to task', icon: <Icon name="link" />, onSelect: () => {} },
              { label: 'Archive', icon: <Icon name="inbox" />, disabled: true, onSelect: () => {} },
              {
                label: 'Delete task',
                icon: <Icon name="trash" />,
                destructive: true,
                onSelect: () => setDialog('destructive'),
                testId: 'gallery_deleteItem',
              },
            ]}
          />
        </div>
      </Section>

      <Section title="Select">
        <Select
          label="Team"
          placeholder="Choose a team"
          helperText="Who works on it"
          options={teams}
          selected={team}
          onSelect={setTeam}
          optionKey={(it) => it}
          optionLabel={(it) => it}
          testId="gallery_teamField"
          optionTestId={(it) => `gallery_teamItem_${it.toLowerCase()}`}
        />
        <Select
          label="Team, chosen"
          size="sm"
          options={teams}
          selected={chosenTeam}
          onSelect={setChosenTeam}
          optionKey={(it) => it}
          optionLabel={(it) => it}
        />
        <Select
          label="Assignee"
          placeholder="Choose someone"
          size="lg"
          options={['Ada', 'Grace', 'Linus']}
          selected={assignee}
          onSelect={setAssignee}
          optionKey={(it) => it}
          optionLabel={(it) => it}
          errorText={assignee ? undefined : 'Choose someone'}
        />
        <Select
          label="Role"
          options={['Owner', 'Member']}
          selected="Member"
          onSelect={() => {}}
          optionKey={(it) => it}
          optionLabel={(it) => it}
          disabled
        />
        <Select
          label="Team, with its dot"
          options={teams}
          selected={dotTeam}
          onSelect={setDotTeam}
          optionKey={(it) => it}
          optionLabel={(it) => it}
          optionLeading={(it) => (
            <span aria-hidden className={`size-2.5 rounded-full ${DOTS[teams.indexOf(it) + 1]}`} />
          )}
          noneLabel="No team"
          onNone={() => setDotTeam(null)}
          testId="gallery_dotTeamField"
        />
        <Select
          label="Team, locked"
          options={teams}
          selected="Kitchen"
          onSelect={() => {}}
          optionKey={(it) => it}
          optionLabel={(it) => it}
          disabledReason="Only a manager can move it"
          testId="gallery_lockedTeamField"
        />
      </Section>

      <Section title="Sheet">
        <div className="flex flex-wrap gap-(--inline-md) pb-(--press-travel)">
          <Button variant="secondary" onClick={() => setSheet('plain')}>
            Open sheet
          </Button>
          <Button variant="tonal" onClick={() => setSheet('reauth')}>
            Open re-auth sheet
          </Button>
        </div>
        <Sheet
          open={sheet === 'plain'}
          title="Switch organization"
          onDismiss={() => setSheet(null)}
          testId="gallery_orgSheet"
        >
          <ul className="divide-y divide-line">
            <ListRow headline="Kitchen Crew" supporting="Owner" onClick={() => setSheet(null)} />
            <ListRow headline="Night Shift" supporting="Member" onClick={() => setSheet(null)} />
          </ul>
        </Sheet>
        <Sheet open={sheet === 'reauth'} title="Confirm it is you" dismissible={false} onDismiss={() => setSheet(null)}>
          <div className="flex flex-col gap-(--stack-md) px-(--inset-lg) pb-(--press-travel)">
            <p className="text-fg-muted">Sign in again to finish changing the plan.</p>
            <Button onClick={() => setSheet(null)} data-testid="gallery_signInButton">
              Sign in again
            </Button>
          </div>
        </Sheet>
      </Section>

      <Section title="Snackbar">
        <Snackbar message="Task saved" tone="positive" />
        <Snackbar message="Task moved to Done" actionLabel="Undo" />
        <Snackbar message="Could not reach the server" tone="negative" onDismiss={() => {}} />
        <Snackbar message="Two tasks are overdue" tone="warning" />
        <Snackbar message="Saved as a draft" />
        <div className="flex flex-wrap gap-(--inline-md) pb-(--press-travel)">
          <Button variant="secondary" onClick={() => showSnackbar({ message: 'Task saved', tone: 'positive' })}>
            Show snackbar
          </Button>
          <Button
            variant="secondary"
            onClick={() => showSnackbar({ message: 'Task moved to Done', actionLabel: 'Undo', onAction: () => {} })}
          >
            Show snackbar with action
          </Button>
        </div>
      </Section>

      <Section title="Banner">
        <Banner title="Offline" message="Changes will sync when you are back." testId="gallery_offlineBanner" />
        <Banner
          tone="warning"
          message="This demo ends in 12 minutes"
          actionLabel="Create an account"
          onAction={() => {}}
          actionTestId="gallery_createAccountButton"
        />
        {banners && (
          <Banner
            tone="error"
            title="Suspended"
            message="This organization is suspended"
            onDismiss={() => setBanners(false)}
            dismissTestId="gallery_dismissButton"
          />
        )}
      </Section>

      <Section title="Skeleton">
        <div data-testid="gallery_skeleton" className="flex flex-col gap-(--stack-sm)">
          <Skeleton className="w-3/5" />
          <Skeleton className="h-(--stack-sm) w-1/3" />
          <Skeleton className="size-(--icon-lg) rounded-(--radius-full)" />
        </div>
      </Section>

      <Section title="Progress">
        <Progress value={0} label="Nothing uploaded yet" />
        <Progress value={uploaded / 12} label={`Uploading ${uploaded} of 12 files`} />
        <Progress value={1} label="All 12 files uploaded" />
        <div className="pb-(--press-travel)">
          <Button
            variant="secondary"
            size="sm"
            data-testid="gallery_stepButton"
            onClick={() => setUploaded((it) => (it % 12) + 1)}
          >
            Next file
          </Button>
        </div>
      </Section>

      <Section title="Progress ring">
        <div className="glacier glacier-wide flex flex-wrap gap-(--inline-lg) rounded-(--radius-tile) p-(--inset-lg)">
          <div className="glacier-body flex flex-wrap items-center gap-(--inline-lg)">
            <ProgressRing done={3} total={7} label="today" testId="gallery_dayProgress" />
            <ProgressRing done={7} total={7} label="done today" size={96} testId="gallery_doneProgress" />
            <ProgressRing done={0} total={0} label="nothing due" testId="gallery_emptyProgress" />
          </div>
        </div>
      </Section>

      <Section title="Check">
        <div className="flex items-center gap-(--inline-md) pb-(--press-travel)">
          <Check key={checks} label="Saved" />
          <Button
            variant="secondary"
            size="sm"
            data-testid="gallery_replayButton"
            onClick={() => setChecks(checks + 1)}
          >
            Draw again
          </Button>
        </div>
      </Section>

      <StatusParts Section={Section} />
      <StateParts Section={Section} />
      <AccessParts Section={Section} />
      <NumberParts Section={Section} />
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-(--stack-md)">
      <h2 className="font-bold text-fg text-body-lg">{title}</h2>
      {children}
    </section>
  )
}

/** The glacier header's ground under a glass control, its texture behind it, as the header draws it in either theme. */
function HeaderGround({ on = true, children }: { on?: boolean; children: ReactNode }) {
  if (!on) return children
  return (
    <span className="glacier inline-flex rounded-full p-1.5">
      <span className="glacier-body inline-flex">{children}</span>
    </span>
  )
}
